<template>
  <header>
    <p>
      Estado:
      <a-tag :color="getStatusColor(record.status)">
        {{ getStatusText(record.status) }}
      </a-tag>
    </p>

    <div v-if="record?.created_at">
      <InfoCircleOutlined />

      <span class="time__send">
        Credenciales creadas el {{ dateToString(record?.created_at, humanDateTimeWithSlashFormat) }}
      </span>
    </div>
  </header>

  <section>
    <a-form layout="vertical">
      <a-form-item label="Correo">
        <a-input :value="record.email" disabled>
          <template #suffix>
            <CopyButton :value="record.email" />
          </template>
        </a-input>
      </a-form-item>

      <a-form-item label="Contraseña">
        <a-input :value="record.token" disabled>
          <template #suffix>
            <CopyButton :value="record.token" />
          </template>
        </a-input>
      </a-form-item>

      <a-form-item label="Tipo de perfil asignado" required>
        <ProfilePicker v-model:value="selectedProfile" />
      </a-form-item>
    </a-form>

    <p class="info">
      El usuario pasará al estado Activo en el momento que ingrese por primera vez a su cuenta y
      complete sus datos personales.
    </p>
  </section>

  <a-divider type="horizontal" class="divider" />

  <footer>
    <a-button class="button-link" type="link" danger @click="emit('delete')">
      Eliminar usuario
    </a-button>

    <div>
      <a-button @click="emit('cancel')" style="margin-right: 12px"> Cancelar </a-button>
      <a-button
        type="primary"
        @click="handleOK"
        :disabled="selectedProfile === record.profile"
        :loading="saving"
      >
        Actualizar información
      </a-button>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import { message } from "ant-design-vue";

import type { UserData } from "@/@types/global";
import { UserStatus } from "@/app/shared/utils/enums";
import { dateToString } from "@/app/shared/utils/dates";
import { humanDateTimeWithSlashFormat } from "@/app/shared/utils/constants";
import { updateUser } from "@/app/users/services";

import ProfilePicker from "@/app/shared/components/pickers/ProfilePicker.vue";
import CopyButton from "@/app/shared/components/buttons/CopyButton.vue";

const props = defineProps<{ record: UserData }>();

const emit = defineEmits<{
  (event: "cancel"): void;
  (event: "delete"): void;
  (event: "update", show: boolean): void;
  (event: "update:table"): void;
}>();

const getStatusText = (status: number) => {
  let value;
  switch (status) {
    case UserStatus.active:
      value = "Activo";
      break;
    case UserStatus.inactive:
      value = "Inactivo";
      break;
    case UserStatus.delete:
      value = "Eliminado";
      break;
    case UserStatus.pending:
      value = "Pendiente";
      break;
  }
  return value;
};

const getStatusColor = (status: number) => {
  let value;
  switch (status) {
    case UserStatus.active:
      value = "green";
      break;
    case UserStatus.inactive:
      value = "red";
      break;
    case UserStatus.delete:
      value = "red";
      break;
    case UserStatus.pending:
      value = "default";
      break;
  }
  return value;
};

const saving = ref(false);
const selectedProfile = ref(props.record.profile);

watch(selectedProfile, (newProfile) => {
  if (newProfile !== props.record.profile) emit("update", true);
  else emit("update", false);
});

const handleOK = async () => {
  saving.value = true;

  try {
    const body = new FormData();
    body.append("profile", String(selectedProfile.value));

    await updateUser(props.record.id, body);

    message.success("Se guardaron los cambios");
    emit("update", false);
    emit("update:table");
    emit("cancel");
  } catch (error) {
    console.error(error);
    message.success("Ha ocurrido un error");
  }

  saving.value = false;
};
</script>

<style scoped lang="sass">
p
  margin: 0

header
  display: flex
  align-items: center
  gap: 10px

.time__send
  font-style: italic

section
  display: flex
  flex-direction: column
  justify-content: space-between
  height: calc(100% - 88px)
  padding: 35px 25px

span, .info
  color: $gray-8

.divider
  background-color: $gray-5
  position: relative
  width: calc(100% + 50px)
  left: -25px
  margin: 0

footer
  display: flex
  justify-content: space-between
  align-items: center
  width: 100%
  padding: 16px 0

  .button-link
    padding: 0
</style>
